import React from 'react';

/**
 * A factory for the stroke-icon wrapper both scheduling surfaces draw with.
 *
 * The glyph paths were already shared (`shared/strokeGlyphs.jsx`); the 19-line `<svg>` around them was
 * copied instead, and the two copies had already drifted -- different default sizes, and only one of
 * them carried the `role="img"` / `<title>` branch. That is not cosmetic: without it an icon can only
 * ever be decorative, so the copy on the applicant-facing booking page could not be given an
 * accessible name even where it needed one.
 *
 * Kept as a factory rather than one component because the two callers legitimately differ: they have
 * different glyph sets and different default sizes, and the booking page cannot load the ATS sprite
 * (`core/Icon`) at all -- which is why neither uses it.
 *
 * `title` is what decides the accessibility contract, and that is deliberate: an icon with a name is
 * announced as an image with that name, and an icon without one is hidden from assistive technology
 * entirely. Both are correct; the mistake is a third state where a meaningful icon is silently
 * skipped, which is what the un-branched copy produced.
 */
export const makeStrokeIcon = (paths, { defaultSize = 16, defaultStrokeWidth = 1.7 } = {}) => {
    const StrokeIcon = ({ name, size = defaultSize, strokeWidth = defaultStrokeWidth, title = null, className }) => (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            focusable="false"
            aria-hidden={title ? undefined : 'true'}
            role={title ? 'img' : undefined}
        >
            {title && <title>{title}</title>}
            {paths[name]}
        </svg>
    );
    return StrokeIcon;
};

export default makeStrokeIcon;
